import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
// services
import { SharedService } from 'src/app/shared/services/shared.service';
import { DummyDataService } from 'src/app/shared/services/dummy-data/dummy-data.service';
import { ContextMenuService } from 'src/app/shared/services/context-menu.service';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  RowSelectionOptions,
  CellContextMenuEvent,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// models
import {
  Answers,
  ModalType,
  Questions,
} from 'src/app/shared/models/constants';
import { InventoryAlert, AutoPurchase } from 'src/app/shared/models/general';
import { forkJoin } from 'rxjs';
import { AlertColumnDef, AutoPurchaseColumnDef } from './columnDefs';
import { DispoEditBtnsComponent } from 'src/app/shared/components/ag-grid/dispo-edit-btns/dispo-edit-btns.component';
import { InventoryRequestService } from './inventory-request.service';

@Component({
  selector: 'app-inventory-request-lists',
  templateUrl: './inventory-request-lists.component.html',
  styleUrls: ['./inventory-request-lists.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe],
})
export class InventoryRequestListsComponent
  implements OnInit, AfterViewInit
{
  private destroyRef = inject(DestroyRef);
  private currentCompanyId: string = '';
  @ViewChild('alertGrid', { read: ElementRef }) alertGridElement!: ElementRef;
  @ViewChild('autoPurchaseGrid', { read: ElementRef })
  autoPurchaseGridElement!: ElementRef;
  menuState$ = this.contextMenuService.menuState$;
  item_upc: string;
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };

  // ag-grid properties Grid
  gridApi!: GridApi;
  alertGridApi!: GridApi;
  autoPurchaseGridApi!: GridApi;

  alertColumnDefs: ColDef[] = AlertColumnDef;
  autoPurchaseColumnDefs: ColDef[] = AutoPurchaseColumnDef;
  defaultColDef;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,};
  components: any;
  theme = agGridDarkTheme;

  alertsData: InventoryAlert[] = [];
  autoPurchaseData: AutoPurchase[] = [];

  constructor(
    public sharedService: SharedService,
    public dummyDataService: DummyDataService,
    private router: Router,
    private inventoryRequestService: InventoryRequestService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
  ) {
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.gridOptions.headerHeight = 40;
    this.gridOptions.enableCellTextSelection = true;
    this.gridOptions.ensureDomOrder = true;
    this.gridOptions.suppressClipboardPaste = false;
    this.components = {
      editDeleteBtns: DispoEditBtnsComponent,
    };
    this.gridOptions.rowHeight = 25;
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
  }
  ngOnInit(): void {
    this.currentCompanyId = this.sharedService.company_info?.company_id;
    this.addButtonColumn();
    this.refreshAllData();
    this.inventoryRequestService.refreshRequests$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshAllData();
      });

    this.sharedService.yes_no_submit$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response.answer === Answers.yes) {
          if (response.question === Questions.delete_inv_alert) {
            this.deleteAlert(response.optional_details);
          } else if (response.question === Questions.delete_auto_purchase) {
            this.deleteAutoOrder(response.optional_details);
          }
        }
      });

    this.sharedService.company_info_retrieved$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((info) => {
        if (info && info.company_id) {
          this.currentCompanyId = info.company_id;
          this.refreshAllData();
        }
      });
  }

  ngAfterViewInit() {
    // Initializing with one of the grids, the service handles it
    this.contextMenuService.initialize(this.cdr, this.alertGridElement);
  }

  /** Loads both grids under a single spinner cycle — calling
   * refreshAlertsData() and refreshAutoPurchasesData() independently would
   * each open/close the same non-ref-counted global spinner, so whichever
   * request resolved first would hide it while the other was still loading. */
  refreshAllData(): void {
    const companyId =
      this.sharedService.company_info?.company_id || this.currentCompanyId;
    if (!companyId) return;
    this.sharedService.openStatusSpinner('Loading Inventory Requests');
    forkJoin({
      alerts: this.inventoryRequestService.getInventoryAlerts(companyId),
      autoPurchases: this.inventoryRequestService.getAutoPurchases(companyId),
    }).subscribe({
      next: ({ alerts, autoPurchases }) => {
        this.alertsData = alerts.data;
        this.autoPurchaseData = autoPurchases.data;
        this.sharedService.closeStatusSpinner();
      },
      error: (err) => {
        console.error('Failed to load inventory requests', err);
        this.sharedService.closeStatusSpinner();
      },
    });
  }

  refreshAlertsData(): void {
    const companyId =
      this.sharedService.company_info?.company_id || this.currentCompanyId;
    if (!companyId) return;
    this.sharedService.openStatusSpinner('Refreshing Inventory Alerts');
    this.inventoryRequestService.getInventoryAlerts(companyId).subscribe({
      next: (res) => {
        this.alertsData = res.data;
        this.sharedService.closeStatusSpinner();
      },
      error: (err) => {
        console.error('Failed to load alerts', err);
        this.sharedService.closeStatusSpinner();
      },
    });
  }

  refreshAutoPurchasesData(): void {
    const companyId =
      this.sharedService.company_info?.company_id || this.currentCompanyId;
    if (!companyId) return;
    this.sharedService.openStatusSpinner('Refreshing Auto Purchases');
    this.inventoryRequestService.getAutoPurchases(companyId).subscribe({
      next: (res) => {
        this.autoPurchaseData = res.data;
        this.sharedService.closeStatusSpinner();
      },
      error: (err) => {
        console.error('Failed to load auto purchases', err);
        this.sharedService.closeStatusSpinner();
      },
    });
  }

  addButtonColumn() {
    const columnDefs = [this.alertColumnDefs, this.autoPurchaseColumnDefs];
    for (const colDef of columnDefs) {
      const hasEditDelete = colDef.some(
        (column) => column.headerName === 'Edit / Delete',
      );
      if (colDef === this.alertColumnDefs) {
        if (!hasEditDelete) {
          colDef.push({
            // Edit / Delete
            headerName: 'Edit / Delete',
            field: 'edit_delete',
            resizable: true,
            width: 140,
            pinned: 'right',
            cellRenderer: 'editDeleteBtns',
            cellRendererParams: {
              editFirearm: this.editAlertClicked.bind(this),
              deleteFirearm: this.deleteAlertClicked.bind(this),
            },
          });
        }
      } else {
        if (!hasEditDelete) {
          colDef.push({
            // Edit / Delete
            headerName: 'Edit / Delete',
            field: 'edit_delete',
            resizable: true,
            width: 140,
            pinned: 'right',
            cellRenderer: 'editDeleteBtns',
            cellRendererParams: {
              editFirearm: this.editAutoOrderClicked.bind(this),
              deleteFirearm: this.deleteAutoOrderClicked.bind(this),
            },
          });
        }
      }
    }
  }
  onAlertGridReady(params: any) {
    this.alertGridApi = params.api;
  }

  onAutoPurchaseGridReady(params: any) {
    this.autoPurchaseGridApi = params.api;
  }

  // button click functions
  editAlertClicked(selected_row: {
    colValues: InventoryAlert;
    rowIndex: number;
  }) {
    this.sharedService.openInventoryOptionsModal(
      'alert',
      ModalType.edit,
      selected_row['colValues'],
    );
  }
  addNewAlertClicked() {
    this.sharedService.openInventoryOptionsModal('alert', ModalType.add);
  }
  editAutoOrderClicked(selected_row: {
    colValues: AutoPurchase;
    rowIndex: number;
  }) {
    this.sharedService.openInventoryOptionsModal(
      'auto_purchase',
      ModalType.edit,
      selected_row['colValues'],
    );
  }
  addNewAutoPurchaseClicked() {
    this.sharedService.openInventoryOptionsModal(
      'auto_purchase',
      ModalType.add,
    );
  }
  deleteAlertClicked(selected_row: {
    colValues: InventoryAlert;
    rowIndex: number;
  }) {
    this.sharedService.openYesNoModal(
      Questions.delete_inv_alert,
      selected_row['colValues'],
    );
  }
  deleteAutoOrderClicked(selected_row: {
    colValues: AutoPurchase;
    rowIndex: number;
  }) {
    this.sharedService.openYesNoModal(
      Questions.delete_auto_purchase,
      selected_row['colValues'],
    );
  }

  deleteAlert(alert: InventoryAlert) {
    if ((alert as any)._id) {
      this.inventoryRequestService
        .deleteInventoryAlert((alert as any)._id)
        .subscribe({
          next: () => {
            this.sharedService.raiseSuccessToast(
              'Inventory Alert deleted successfully!',
            );
            this.refreshAlertsData();
          },
          error: (err) => {
            this.sharedService.raiseErrorToast(
              'Failed to delete Inventory Alert',
            );
          },
        });
    }
  }

  deleteAutoOrder(autoPurchase: AutoPurchase) {
    if ((autoPurchase as any)._id) {
      this.inventoryRequestService
        .deleteAutoPurchase((autoPurchase as any)._id)
        .subscribe({
          next: () => {
            this.sharedService.raiseSuccessToast(
              'Auto Purchase deleted successfully!',
            );
            this.refreshAutoPurchasesData();
          },
          error: (err) => {
            this.sharedService.raiseErrorToast(
              'Failed to delete Auto Purchase',
            );
          },
        });
    }
  }

  openAutoPurchaseHistory() {
    this.router.navigate(['auto-purchase-history']);
  }
  openVendorDashboard() {
    this.router.navigate(['vendor-dashboard']);
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
}
